import { ListedInstrument, getInstrumentIdForce } from '@eih-netbroker/common';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { useMarkets } from '../../../../shared/services/useMarkets';
import { ProductDetailsSectionItem } from '../../components/product/ProductDetailsSection';
import { ProductDetailsBase, productDetailstStateAtom } from '../../states/productDetailsStateAtom';
import { useProductDetailsCommon } from './useProductDetailsCommon';

export type ProductDetailsBaseVisibleFields = Omit<
  ProductDetailsBase,
  'previousLastPrice' | 'isLoading' | 'instrumentStoreItem' | 'newHighOrLowFlag' | 'highNumber' | 'lowNumber'
>;

export const tradingFieldsAll: Array<keyof ProductDetailsBaseVisibleFields> = [
  'openPrice',
  'low',
  'high',
  'averagePrice',
  'lastPrice',
  'closePrice',
  'dailyChange',
  'dailyChangePercent',
  'YTDChangePercent',
  'annualLow',
  'annualHigh',
  'volumeQuantity',
  'volumeValue',
];

export const fundamentalFieldsAll: Array<keyof ProductDetailsBaseVisibleFields> = [
  'marketCapitalization',
  'earningsPerShareTTM',
  'priceToBookValue',
  'priceToEarningsTTM',
  'priceToSalesTTM',
  'dividend',
  'dividendYield',
  'dividendYield5Years',
  'EBITDA',
  'ROE',
  'ROA',
];

export const useProductDetailsListForShare = (instrument: ListedInstrument | null, col: number) => {
  const [product] = useAtom(productDetailstStateAtom(instrument ? getInstrumentIdForce(instrument) : ''));
  const { translation } = useLocale();
  const { unknownValue, getTradingOrFundamentalSection, getInstrumentSection } = useProductDetailsCommon(instrument);
  const { getMicName } = useMarkets();

  const tradingFieldsForShare = useMemo(
    () => getTradingOrFundamentalSection(col, product, tradingFieldsAll),
    [col, product, getTradingOrFundamentalSection],
  );

  const fundamentalFieldsForShare = useMemo(
    () => getTradingOrFundamentalSection(col, product, fundamentalFieldsAll),
    [col, product, getTradingOrFundamentalSection],
  );

  const generalSectionItems: Array<ProductDetailsSectionItem> = useMemo(
    () => [
      {
        id: translation.productDetails.instrumentFields.name,
        label: translation.productDetails.instrumentFields.name,
        value: instrument ? instrument.instrumentName : unknownValue,
      },
      {
        id: translation.productDetails.instrumentFields.isin,
        label: translation.productDetails.instrumentFields.isin,
        value: instrument ? instrument.isin : unknownValue,
      },
      {
        id: translation.productDetails.instrumentFields.instrumentCategory,
        label: translation.productDetails.instrumentFields.instrumentCategory,
        value: instrument ? instrument.instrumentCategory : unknownValue,
      },
      {
        id: translation.productDetails.instrumentFields.marketCode,
        label: translation.productDetails.instrumentFields.marketCode,
        value: instrument ? getMicName(instrument.mic) : unknownValue,
      },
      {
        id: translation.productDetails.instrumentFields.currencyCode,
        label: translation.productDetails.instrumentFields.currencyCode,
        value: instrument ? instrument.currency : unknownValue,
      },
    ],
    [translation, instrument, unknownValue, getMicName],
  );

  const instrumentFieldsForShare = useMemo(
    () => getInstrumentSection(col, product, generalSectionItems),
    [col, product, getInstrumentSection, generalSectionItems],
  );

  return {
    tradingFieldsForShare,
    fundamentalFieldsForShare,
    instrumentFieldsForShare,
  };
};
